import type { PushSubscriptionJson } from '@malash-office/types';

/**
 * The browser half of subscribing (docs/product-planning/PWA-PUSH.md §3). `subscribeThisDevice`
 * must run inside the click handler: iOS only shows the permission prompt for a user gesture.
 */

const urlBase64ToUint8Array = (base64: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, char => char.charCodeAt(0));
};

/** The worker is registered at startup in production; elsewhere (dev) register it now. */
const workerRegistration = async (): Promise<ServiceWorkerRegistration> => {
  const existing = await navigator.serviceWorker.getRegistration('/');
  if (!existing) await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  return navigator.serviceWorker.ready;
};

export class PushPermissionError extends Error {}

export const subscribeThisDevice = async (getPublicKey: () => Promise<string>): Promise<PushSubscriptionJson> => {
  // Asked first and directly — anything awaited before it can cost the gesture on iOS.
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new PushPermissionError('לא אושרו התראות. אפשר לאשר בהגדרות הטלפון ← התראות.');
  }
  const [registration, publicKey] = await Promise.all([workerRegistration(), getPublicKey()]);
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey)
    }));
  return subscription.toJSON() as PushSubscriptionJson;
};

/** This device's current endpoint, if it is subscribed — to mark "המכשיר הזה" in the list. */
export const currentEndpoint = async (): Promise<string | null> => {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  const registration = await navigator.serviceWorker.getRegistration('/');
  const subscription = await registration?.pushManager?.getSubscription();
  return subscription?.endpoint ?? null;
};

/**
 * On sign-out: tell the server to forget this device, then drop the browser's subscription, so a
 * shared phone stops receiving the previous user's pushes. Never throws — signing out must work.
 */
export const forgetThisDevice = async (tellServer: (endpoint: string) => Promise<unknown>): Promise<void> => {
  try {
    if (!('serviceWorker' in navigator)) return;
    const registration = await navigator.serviceWorker.getRegistration('/');
    const subscription = await registration?.pushManager?.getSubscription();
    if (!subscription) return;
    await tellServer(subscription.endpoint).catch(() => undefined);
    await subscription.unsubscribe();
  } catch {
    // Nothing to do: the server drops a dead endpoint itself on the next send (410).
  }
};
